import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { DATETIME_DOTS_FORMAT } from '@/common/constants';
import { useCurrentOrganization, useGetOrganizationById } from '@/modules/core/hooks';
import { useDownloadReportQuery } from '@/modules/Reports/queries/useDownloadReport.query';
import { ReportTypes } from '@/modules/Reports/report.types';
import { getIdsFromPath } from '@/utils';
import downloadFileByUrl from '@/utils/handlers/downloadFileByUrl';

interface Props {
  invoiceId?: string;
  currentReportType: ReportTypes | null;
  invoiceOrganizationId: string;
}

export const useDownloadInvoiceReport = ({ invoiceId, currentReportType, invoiceOrganizationId }: Props) => {
  const [isStartDownload, setIsStartDownload] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  const {
    observingOrganization: { id: orgId },
  } = useCurrentOrganization();
  const { formattedData } = useGetOrganizationById(invoiceOrganizationId);

  const { employerId } = useMemo(() => getIdsFromPath(formattedData.path), [formattedData.path]);

  const currentCustomExportParams = useMemo(
    () => ({
      'invoice-ids': invoiceId || '',
    }),
    [invoiceId]
  );

  const {
    data,
    fileName,
    remove,
    isLoading: reportLoading,
    isError,
  } = useDownloadReportQuery(
    employerId || (orgId as string),
    isStartDownload,
    currentReportType,
    currentCustomExportParams,
    invoiceId
  );

  useEffect(() => {
    if (data && isStartDownload) {
      const currentDate = dayjs(Date.now()).format(DATETIME_DOTS_FORMAT);
      const fileExtension = 'csv';
      const currentFileName = `${fileName}_${currentDate}.${fileExtension}`;
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      downloadFileByUrl([url], currentFileName);
      setIsStartDownload(false);
      remove();
    }
  }, [data, fileName, isStartDownload, remove]);

  useEffect(() => {
    if (isError) {
      setIsServerError(true);
    }
  }, [isError]);

  return {
    setIsStartDownload,
    reportLoading,
    isServerError,
    setIsServerError,
  };
};
